import * as Sentry from '@sentry/react';

import { useQuery } from '@tanstack/react-query';

import { useRisClient } from './useRisClient';

import { notEmpty } from 'utils/common';

import { PartnerCloudUserAuthResponse } from 'types/types';

export function useAccounts() {
  const { client } = useRisClient();

  return useQuery({
    queryKey: ['dataStream', 'accounts'],
    queryFn: async () => {
      const { data } =
        await client.partnerClouds.myUsersGetPartnerCloudDataStreamingDetails();

      if (!data) return [];

      return data
        .map(account => {
          if (!account.partnerCloudId) {
            Sentry.captureEvent({
              message: '[Data Streaming] Invalid partner account payload',
              level: 'error',
              extra: { account },
            });

            return null;
          }

          return {
            partnerCloudId: account.partnerCloudId,
            partnerCloudCode: account.partnerCloudCode || 'Unknown',
            partnerCloudName: account.partnerCloudName || 'Unknown Partner',
            connectedDevices: Array.isArray(account.connectedDevices)
              ? account.connectedDevices
              : [],
            loginUrl: account.loginUrl || '',
          };
        })
        .filter(notEmpty);
    },
    select: data => ({
      available: data.filter(account => !account.connectedDevices.length),
      connected: data.filter(account => account.connectedDevices.length),
    }),

    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useDevices(
  partnerCloudId: string,
  session: PartnerCloudUserAuthResponse | undefined
) {
  const { client } = useRisClient();

  return useQuery({
    queryKey: [
      'dataStream',
      'devices',
      partnerCloudId,
      session?.externalUserId,
    ],
    enabled: !!session, // Only run query when session is available
    queryFn: async () => {
      if (!session) {
        throw new Error('User Session is not defined');
      }

      const { data } = await client.partnerClouds.myUsersGetPartnerCloudDevices(
        partnerCloudId,
        session.accessToken,
        session.externalUserId,
        session.encryptedDeviceData
      );

      if (!data) return [];

      return data
        .map(device => {
          if (!device.deviceId || !device.externalDeviceId) {
            Sentry.captureEvent({
              message: '[Data Streaming] Invalid partner device payload',
              level: 'error',
              extra: { device },
            });

            return null;
          }

          return {
            deviceId: device.deviceId,
            externalDeviceId: device.externalDeviceId,
            deviceName: device.deviceName || 'Unknown Device',
            deviceModel: device.deviceModel || 'Unknown Model',
            deviceType: device.deviceType || 'Unknown Type',
            isDataStreamingEnabled: device.isDataStreamingEnabled || false,
          };
        })
        .filter(notEmpty);
    },

    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useConnectedDevices(partnerCloudId: string) {
  const { client } = useRisClient();

  return useQuery({
    queryKey: ['dataStream', 'connectedDevices', partnerCloudId],
    queryFn: async () => {
      const { data } =
        await client.partnerClouds.myUsersGetPartnerCloudDataStreamingDevices(
          partnerCloudId
        );

      if (!data) return [];

      return data
        .map(device => {
          if (!device.deviceId || !device.externalDeviceId) {
            Sentry.captureEvent({
              message:
                '[Data Streaming] Invalid partner connected device payload',
              level: 'error',
              extra: { device },
            });

            return null;
          }

          return {
            deviceId: device.deviceId,
            externalDeviceId: device.externalDeviceId,
            deviceName: device.deviceName || 'Unknown Device',
            deviceModel: device.deviceModel || 'Unknown Model',
            deviceType: device.deviceType || 'Unknown Type',
          };
        })
        .filter(notEmpty);
    },

    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useWebhook() {
  const { client } = useRisClient();

  return useQuery({
    queryKey: ['dataStream', 'webhooks'],
    queryFn: async () => {
      const { data } = await client.myUsers.myUsersGetWebhooks();

      if (!data || !data.length) return null;

      const webhook = data[0];

      if (!webhook.id || !webhook.callbackUrl) {
        Sentry.captureEvent({
          message: '[Data Streaming] Invalid webhook payload',
          level: 'error',
          extra: { webhook: data },
        });

        return null;
      }

      return {
        id: webhook.id,
        callbackUrl: webhook.callbackUrl,
        headers: Array.isArray(webhook.headers) ? webhook.headers : [],
        isEnabled: webhook.isEnabled,
      };
    },

    staleTime: 30 * 60 * 1000,
    retry: false,
  });
}
